import { Card } from '@common/components';
import { Flex, Text, Title, Tooltip, UnstyledButton } from '@mantine/core';
import { ReactComponent as LogoSimple } from '../../assets/svg/logo-simple.svg';
import { Copy } from '@geist-ui/icons';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import { useEffect, useRef, useState } from 'react';
import { setTooltipTimeout } from 'src/helpers/setTooltipTimeout';
import { useAlert } from '@common/hooks';
import { useDocumentTitle } from '@mantine/hooks';

const SupportPage = (): JSX.Element => {
  const [t] = useTranslation('support');
  const [opened, setOpened] = useState(false);
  const { showAlert } = useAlert();
  const [tooltipId, setTooltipId] = useState('');
  const mainHeading = useRef<HTMLHeadingElement>(null)
  useDocumentTitle(t('title'));
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (opened) {
      setTooltipTimeout(2000).then(() => {
        setOpened(false);
      });
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [opened]);

  const handleCopyClick = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(`${t(text)}`);
      setOpened(true);
      setTooltipId(text);
    } catch (err) {
      showAlert({
        id: 'copy-error',
        title: t('errorTitle'),
        message: `${t('failedToCopy')}`,
        variant: 'error',
      });
    }
  };
  useEffect(() => {
    if ( mainHeading?.current ){
      mainHeading?.current?.setAttribute('tabindex', '-1');
      mainHeading?.current?.setAttribute('tabindex', '0');
      mainHeading?.current?.focus();
    }
  } , [])
  return (
    <Flex style={{ flexGrow: 1 ,  width : 'calc(100% - 300px)' }} direction={'column'} bg={'#F6FAFA'} my={0} px={24} py={24}>
      <Title m={12} id={'mainContent'} color={'#264547'} ref={mainHeading}>
        {t('title')}
      </Title>
      <Flex wrap={'wrap'} gap={'16px'} rowGap={'16px'}>
        <Card className={styles.card} titleOrder={2} title={`${t('cardSupport.title')}`} isDivider={false}>
          <Flex className={styles.contactContent}>
            <LogoSimple aria-hidden="true" />
            <div>
              <Text weight={700} size={18} py={4}>
                {t('cardSupport.content')}
              </Text>
              <Flex w={"100%"} justify={"space-between"}>
                <Text weight={500} size={16} py={4}>
                  {t('cardSupport.email')}
                </Text>
                <Tooltip
                  events={{ hover: false, focus: true, touch: false }}
                  label={`${t('emailCopied')}`}
                  opened={opened && tooltipId === 'cardSupport.email'}
                  withArrow>
                  <Flex className={styles.buttonCopy}>
                    <UnstyledButton
                      aria-label={`${t('buttonCopyEmail')}`}
                      onClick={() => handleCopyClick('cardSupport.email')}>
                      <Copy size={44} />
                    </UnstyledButton>
                  </Flex>
                </Tooltip>
              </Flex>
              <Flex w={"100%"} justify={"space-between"}>
                <Text weight={500} size={16} py={4}>
                  {t('cardSupport.phone')}
                </Text>
                <Tooltip
                  events={{ hover: false, focus: true, touch: true }}
                  label={`${t('phoneCopied')}`}
                  opened={opened && tooltipId === 'cardSupport.phone'}
                  withArrow>
                  <Flex className={styles.buttonCopy}>
                    <UnstyledButton
                      aria-label={`${t('buttonCopyPhone')}`}
                      onClick={() => handleCopyClick('cardSupport.phone')}>
                      <Copy size={44} />
                    </UnstyledButton>
                  </Flex>
                </Tooltip>
              </Flex>
            </div>
          </Flex>
          <Text py={6}>{t('cardSupport.availability')}</Text>
        </Card>
        <Card className={styles.card} titleOrder={2} title={`${t('cardManualAudit.title')}`} isDivider={false}>
          <Flex className={styles.contactContent}>
            <LogoSimple aria-hidden="true" />
            <div>
              <Text size={18} weight={700} py={4}>
                {t('cardManualAudit.name')}
              </Text>
              <Text size={16} py={4}>
                {t('cardManualAudit.jobTitle')}
              </Text>
              <Flex w={"100%"} justify={"space-between"} >
                <Text size={16} weight={500} py={4}>
                  {t('cardManualAudit.email')}
                </Text>
                <Tooltip
                  label={`${t('emailCopied')}`}
                  events={{ hover: false, focus: true, touch: true }}
                  opened={opened && tooltipId === 'cardManualAudit.email'}
                  withArrow>
                  <Flex className={styles.buttonCopy}>
                    <UnstyledButton
                      aria-label={`${t('buttonCopyEmail')}`}
                      onClick={() => handleCopyClick('cardManualAudit.email')}>
                      <Copy size={44}  />
                    </UnstyledButton>
                  </Flex>
                </Tooltip>
              </Flex>
            </div>
          </Flex>
          <Text py={4}>{t('cardManualAudit.content')}</Text>
        </Card>
        <Card className={styles.card} titleOrder={2} title={`${t('cardExpertConsultation.title')}`} isDivider={false}>
          <Flex className={styles.contactContent}>
            <LogoSimple aria-hidden="true" />
            <div>
              <Text size={18} weight={700} py={4}>
                {t('cardExpertConsultation.name')}
              </Text>
              <Text size={16} py={4}>
                {t('cardExpertConsultation.jobTitle')}
              </Text>
              <Flex w={"100%"} justify={"space-between"}>
                <Text size={16} weight={500} py={4}>
                  {t('cardExpertConsultation.email')}
                </Text>
                <Tooltip
                  label={`${t('emailCopied')}`}
                  events={{ hover: false, focus: true, touch: true }}
                  opened={opened && tooltipId === 'cardExpertConsultation.email'}
                  withArrow>
                  <Flex className={styles.buttonCopy}>
                    <UnstyledButton
                      aria-label={`${t('buttonCopyEmail')}`}
                      onClick={() => handleCopyClick('cardExpertConsultation.email')}>
                      <Copy size={44} />
                    </UnstyledButton>
                  </Flex>
                </Tooltip>
              </Flex>
            </div>
          </Flex>
          <Text py={4}>{t('cardExpertConsultation.availability')}</Text>
        </Card>
        {opened && (
          <div role="alert" aria-live="assertive" className={styles['visually-hidden']}>
            <span>{tooltipId.includes('email') ? t('emailCopied') : t('phoneCopied')}</span>
          </div>
        )}
      </Flex>
    </Flex>
  );
};

export default SupportPage;
